import { Box } from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { colors, tokens } from "../../../../utils";
import { ASSETS } from "../../../../utils/config";
import ImageCropper from "../../../ImageCropper";
import Input from "../../../Input";
import TextView from "../../../TextView";
import ReactPlayer from "react-player";
import { toast } from "react-toastify";
import { getVideoUrl } from "../../CodeBlocks/Video";
import { FILL_ERROR_ICON_COLOR } from "../../../../renderer-v2/fillErrorStyle";

const Container = styled.div`
  width: 100%;
  background-color: ${colors.background};
  border-radius: 8px;
  padding: 16px;
  border: 1px solid ${colors.lightBorder};
  margin-bottom: 20px;
`;

const Icon = styled.img`
  cursor: pointer;
  margin-left: 10px;
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: ${(props) => (props.alignTop ? "flex-start" : "center")};
`;

const MediaPreview = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 4px;
  margin-right: 16px;
  overflow: hidden;
  object-fit: cover;
  border: 1px solid ${colors.lightGrey};
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${colors.lightGrey};
  flex-shrink: 0; // Prevent thumbnail from shrinking
`;

const Iframe = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
  border-radius: 4px;
`;

// gordon-0923-P0-editwheel-delta-C: a broken/CORS-blocked media URL (e.g. a
// stale "Reuse from your Media Library" pick) used to leave this thumbnail a
// blank box with zero feedback -- an author could not tell their pick failed.
// View Wheel already gives a dead wedge fill this exact calm, non-alarm
// treatment (renderer-v2/fillErrorStyle.js, PR#6057): same ink color, same
// "Image could not load" wording, same line-art picture-frame glyph (never an
// emoji, never red). This reuses that language for the shared BUILDER-side
// preview so every one of its ~15 consumer surfaces (Portrait, Wallpaper,
// every segment media block) gets it for free.
const BrokenMediaFallback = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  width: 100%;
  height: 100%;
  color: ${FILL_ERROR_ICON_COLOR};
  text-align: center;
  padding: 4px;
`;

const BrokenMediaText = styled.span`
  font-size: 9px;
  line-height: 1.2;
`;

const BrokenImageIcon = () => (
  <svg width="20" height="20" viewBox="0 0 14 14" aria-hidden="true" focusable="false">
    <rect
      x="1"
      y="1"
      width="12"
      height="12"
      rx="2"
      fill="none"
      stroke={FILL_ERROR_ICON_COLOR}
    />
    <circle cx="4.5" cy="4.5" r="1.1" fill={FILL_ERROR_ICON_COLOR} />
    <line x1="1.5" y1="12.5" x2="12.5" y2="1.5" stroke={FILL_ERROR_ICON_COLOR} strokeLinecap="round" />
  </svg>
);

const FilenameContainer = styled.div`
  width: 100%;
  padding-right: ${(props) => (props.hasIcons ? "10px" : "0")};
`;

const Filename = styled(TextView)`
  text-align: left;
  overflow-wrap: break-word;
  word-wrap: break-word;
  word-break: break-word;
  hyphens: auto;
  line-height: 1.4;
  max-width: 100%;
`;

const URLContainer = styled.div`
  margin-top: 16px;
  padding: 6px;
  background-color: white;
  border-radius: 8px;
  border: 1px solid ${colors.lightBorder};
`;

const URLRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 24px;
`;

const URLInput = styled.input`
  flex: 1;
  padding: 0px;
  border-radius: 4px;
  font-size: 14px;
  background-color: #fff;
  color: ${colors.darkGrey};
  border: none;
  outline: none;

  &:focus {
    border-color: none;
  }
`;

const CopyButton = styled.button`
  padding: 8px 16px;
  /* DL-004 (ISP-4018): white-text fill -> on-fill token (#8F38EF, AA 5.28:1). */
  background-color: ${tokens.brand.primaryActive};
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background-color: ${colors.darkPurple};
  }

  &:active {
    transform: translateY(1px);
  }
`;

const UploadedMedia = ({
  type = "image",
  media,
  title,
  onChange,
  showTitle,
  handleCheckbox,
  handleEditMedia,
  originalImage,
  allowEdit = true,
  lockedAspectRatio = null,
  persistCrop = false,
  isTitleCustomizable = allowEdit,
  handleMediaRemove = () => {},
  pathType,
  showURL = true,
}) => {
  const [open, setOpen] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);

  // A freshly-picked media url replaces the broken one -- give the new pick a
  // clean chance to load instead of staying stuck showing the old failure.
  useEffect(() => {
    setImageFailed(false);
  }, [media]);

  const handleCopyURL = async () => {
    try {
      await navigator.clipboard.writeText(media);
      // You could add a toast notification here if needed
      toast.success("URL copied to clipboard");
    } catch (err) {
      console.error("Failed to copy URL:", err);
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = media;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
    }
  };

  const renderMedia = () => {
    switch (type) {
      case "image":
        if (imageFailed) {
          return (
            <BrokenMediaFallback>
              <BrokenImageIcon />
              <BrokenMediaText>Image could not load</BrokenMediaText>
            </BrokenMediaFallback>
          );
        }
        return (
          <img
            src={media}
            alt="uploaded media"
            onError={() => setImageFailed(true)}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        );
      case "video":
        const videoUrl = getVideoUrl(media);
        const isLoomVideo = videoUrl ? videoUrl?.includes("loom.com/embed/") : false;

        return isLoomVideo ? (
          <iframe
            src={videoUrl}
            frameBorder="0"
            allowFullScreen
            style={{ width: "100%", height: "100%" }}
          />
        ) : (
          <ReactPlayer
            url={media}
            width={"100%"}
            height={"100%"}
            playing={false}
          />
        );
      case "document":
        return <Iframe src={media} title="uploaded document" />;
      default:
        return null;
    }
  };

  return (
    <Container>
      <Row>
        <TextView variant="subtitle">
          Uploaded {type.charAt(0).toUpperCase() + type.slice(1)}
        </TextView>
        {allowEdit && (
          <Box>
            {type === "image" && (
              <Icon
                width={20}
                height={20}
                src={ASSETS.EditImage}
                onClick={() => setOpen(true)}
              />
            )}
            <Icon
              width={20}
              height={20}
              src={ASSETS.deleteIcon2}
              onClick={handleMediaRemove}
            />
          </Box>
        )}
      </Row>
      <Row style={{ marginTop: 10 }} alignTop={true}>
        <MediaPreview>{renderMedia()}</MediaPreview>
        {allowEdit && isTitleCustomizable ? (
          <div style={{ width: "100%" }}>
            <Row>
              <Input
                containerStyle={{ width: "100%", marginBottom: 0 }}
                variant={"secondary"}
                value={title}
                onChange={(e) => onChange(e.target.value)}
                maxLength={40}
                placeholder={type === "image" ? "Add image name" : "Add title"}
              />
              <TextView style={{ width: 55, textAlign: "right" }}>
                {title?.length}/40
              </TextView>
            </Row>

            <TextView>
              <Checkbox
                onChange={handleCheckbox}
                checked={showTitle}
                sx={{
                  color: colors.lightPurple,
                  "&.Mui-checked": {
                    color: colors.purple,
                  },
                }}
              />
              Display Caption
            </TextView>
          </div>
        ) : (
          <FilenameContainer hasIcons={allowEdit}>
            <Filename>{title}</Filename>
          </FilenameContainer>
        )}
      </Row>
      {showURL && (
        <URLContainer>
          <URLRow>
            <URLInput
              type="text"
              value={media}
              readOnly
              placeholder="Media URL"
            />
            <CopyButton type="button" onClick={handleCopyURL}>
              Copy URL
            </CopyButton>
          </URLRow>
        </URLContainer>
      )}
      {type === "image" && (
        <ImageCropper
          persistCrop={persistCrop}
          lockedAspectRatio={lockedAspectRatio}
          src={originalImage}
          open={open}
          handleClose={() => setOpen(false)}
          handleEditImage={(val) => handleEditMedia({ src: val })}
          pathType={pathType}
        />
      )}
    </Container>
  );
};

export default UploadedMedia;
